<template>
  <div class="tab-panels">
    <div
      ref="track"
      class="tab-panels-track"
      :style="{
        transform: `translateX(${-active * 100}%)`,
        height: height === undefined ? undefined : `${height}px`,
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Tab bodies that slide sideways and ease to the active panel's height: what the Swiper with
 * `allowTouchMove: false` + `autoHeight` behind each tab strip did, at Swiper's speed (300 ms,
 * `ease`). Each direct child of the slot is one panel: give it `role="tabpanel"` and
 * `aria-labelledby` its tab, which is where focus goes if it was inside a panel being hidden.
 */
import { onBeforeUnmount, onMounted, onUpdated, ref, watch } from 'vue'

const props = defineProps<{ active: number }>()

const track = ref<HTMLElement>()
const height = ref<number>()

const panels = () => Array.from(track.value?.children ?? []) as HTMLElement[]

// Follows the active panel's own size changes too (a date picker opening, validation errors),
// which Swiper only picked up when someone remembered to call updateAutoHeight().
const observer =
  typeof ResizeObserver === 'undefined'
    ? undefined
    : new ResizeObserver(([entry]) => {
        height.value = (entry?.target as HTMLElement).offsetHeight
      })

let observed: HTMLElement | undefined
let refocus = false

// After every render, so panels the slot adds or replaces are covered too.
const sync = () => {
  const active = panels()[props.active]
  // Off-screen panels stay laid out for the slide, but out of the tab order.
  panels().forEach(panel => (panel.inert = panel !== active))
  if (active !== observed) {
    observer?.disconnect()
    observed = active
    if (active) {
      height.value = active.offsetHeight
      observer?.observe(active)
    }
  }
  // A control inside a panel switched tabs (e.g. "choose digital delivery"): its panel is now
  // inert, so hand focus to the new panel's tab instead of letting it fall to <body>.
  if (refocus) {
    refocus = false
    const tabId = active?.getAttribute('aria-labelledby')
    if (tabId) document.getElementById(tabId)?.focus({ preventScroll: true })
  }
}

// Before the render: only the outgoing panel is reachable, so focus in the track is focus in it.
watch(
  () => props.active,
  () => (refocus = !!track.value?.contains(document.activeElement)),
  { flush: 'pre' },
)
onMounted(sync)
onUpdated(sync)
onBeforeUnmount(() => observer?.disconnect())
</script>

<style>
.tab-panels {
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.tab-panels-track {
  display: flex;
  align-items: flex-start;
  transition:
    transform 300ms ease,
    height 300ms ease;
}

.tab-panels-track > * {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  .tab-panels-track {
    transition: none;
  }
}
</style>
